import { useEffect, useRef } from "react"
import { createArticleFromTemplate, isDraft, addDownloadUrlsToArticles, getThumbnailImage } from "./data"
import EventBus from "../utils/EventBus"
import CommunityEventNames from './EventNames'
import { FeatureFlags } from "../config/config"
import { getSiteConfig } from "../community/siteProvider"

export function getForumEmbedConfig(site='safety-community-hub') {
  const { vivaCommunity } = getSiteConfig(site)
  let communityId = undefined, embedUrl = undefined, communityLink = undefined

  if (vivaCommunity) {
    if (FeatureFlags.safetyHubForum && FeatureFlags.safetyHubForumBottler) {
      communityId = vivaCommunity['communityId']
    } else if (FeatureFlags.safetyHubForum && !FeatureFlags.safetyHubForumBottler) {
      communityId = vivaCommunity['nonProdCommunityId']
    }
    if (communityId) {
      embedUrl = `https://engage.cloud.microsoft/embed/groups/${communityId}?header=false&footer=false&theme=light&promptText=What's%20on%20your%20mind%3F&includeFeedInformation=false`
      communityLink = `${vivaCommunity['communityLinkPrefix']}${communityId}`
    }
  }

  return {
    communityId,
    embedUrl,
    communityLink
  }
}


export function useInfiniteScroll(callback, offset = 200) {
  const firing = useRef(false);       // debouncing guard

  useEffect(() => {
    function onScroll() {
      if (firing.current) return;

      const { scrollTop, scrollHeight, clientHeight } =
        document.documentElement;    // <html> element

      const nearBottom = scrollTop + clientHeight >= scrollHeight - offset;

      if (nearBottom) {
        firing.current = true;
        Promise.resolve(callback()).finally(() => {
          firing.current = false;
        });
      }
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [callback, offset]);
}

export const createArticle = async (name, template, fields, beforeHook, afterHook) => {
  beforeHook()
  const body = {
    "@odata.type": "#microsoft.graph.sitePage",
    "description": name,
    "name": name,
    "title": name,
    "pageLayout": "article",
    "promotionKind": "newsPost",
    "showComments": false,
    "showRecommendedPages": false,
  }
  const article = await createArticleFromTemplate(template, body, fields)
  afterHook()
  editArticleInSharepoint(article)
}

export const editArticleMetadata = (article, visibility) => {
  EventBus.emit(CommunityEventNames.OPEN_ADD_EDIT_ARITCLE_DIALOG, {
    articleId: article.guid || article.id,
    webUrl: article.webUrl,
    mode: 'edit',
    article,
    isDraft: article.isDraft,
    visibility
  })
}

export const editArticleInSharepoint = (article) => {
  window.open(article?.webUrl + '?Mode=Edit', '_blank')
}

export const viewArticle = (article) => {
  window.open(article?.webUrl + '?env=Embedded', '_blank')
}

export function removeODataContext(obj) {
  if (Array.isArray(obj)) {
    return obj.map(removeODataContext);
  } else if (obj && typeof obj === 'object') {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      if (!key.endsWith('@odata.context') && key !== 'serverProcessedContent') {
        result[key] = removeODataContext(value);
      }
    }
    return result;
  }
  return obj; // primitive value, return as-is
}

export function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const len = bytes.byteLength;
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function truncateWithEllipsis(str, maxLength) {
  if (typeof str !== 'string') return '';
  if (str.length <= maxLength) return str;
  if (maxLength <= 1) return '…';
  return str.slice(0, maxLength - 1) + '…';
}

export function formatBytes(bytes) {
  if (!bytes) return '0 Bytes';

  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));

  const value = bytes / Math.pow(1024, i);
  const rounded = value.toFixed(i === 0 ? 0 : 1); // No decimals for Bytes

  return `${rounded} ${sizes[i]}`;
}

// 𝗠𝗜𝗠𝗘-type → { label, extension, type }
export const mimeTypeMap = {
  /* ───────────── Images ───────────── */
  "image/jpg":                  { label: "JPEG",                 extension: "jpg",   type: "image" },
  "image/jpeg":                 { label: "JPEG",                 extension: "jpg",   type: "image" },
  "image/png":                  { label: "PNG",                  extension: "png",   type: "image" },
  "image/gif":                  { label: "GIF",                  extension: "gif",   type: "image" },
  "image/webp":                 { label: "WebP",                 extension: "webp",  type: "image" },
  "image/avif":                 { label: "AVIF",                 extension: "avif",  type: "image" },
  "image/svg+xml":              { label: "SVG",                  extension: "svg",   type: "image" },
  "image/bmp":                  { label: "Bitmap",               extension: "bmp",   type: "image" },
  "image/tiff":                 { label: "TIFF",                 extension: "tiff",  type: "image" },
  "image/heif":                 { label: "HEIF",                 extension: "heif",  type: "image" },
  "image/heic":                 { label: "HEIC",                 extension: "heic",  type: "image" },
  "image/jp2":                  { label: "JPEG 2000",            extension: "jp2",   type: "image" },
  "image/jxr":                  { label: "JPEG XR",              extension: "jxr",   type: "image" },
  "image/vnd.adobe.photoshop":  { label: "Photoshop",            extension: "psd",   type: "image" },
  "image/vnd.microsoft.icon":   { label: "Icon",                 extension: "ico",   type: "image" },
  "image/x-icon":               { label: "Icon",                 extension: "ico",   type: "image" },
  "image/x-xbm":                { label: "X BitMap",             extension: "xbm",   type: "image" },
  "image/x-xpixmap":            { label: "X PixMap",             extension: "xpm",   type: "image" },
  "image/x-portable-bitmap":    { label: "Portable Bitmap",      extension: "pbm",   type: "image" },
  "image/x-portable-graymap":   { label: "Portable GrayMap",     extension: "pgm",   type: "image" },
  "image/x-portable-pixmap":    { label: "Portable PixMap",      extension: "ppm",   type: "image" },
  "image/emf":                  { label: "Enhanced Metafile",    extension: "emf",   type: "image" },
  "image/wmf":                  { label: "Windows Metafile",     extension: "wmf",   type: "image" },
  "image/vnd.djvu":             { label: "DjVu",                 extension: "djvu",  type: "image" },

  /* ───────────── Videos ───────────── */
  "video/mp4":                  { label: "MP4",                  extension: "mp4",   type: "video" },
  "video/webm":                 { label: "WebM",                 extension: "webm",  type: "video" },
  "video/ogg":                  { label: "Ogg Video",            extension: "ogv",   type: "video" },
  "video/mpeg":                 { label: "MPEG",                 extension: "mpg",   type: "video" },
  "video/quicktime":            { label: "QuickTime",            extension: "mov",   type: "video" },
  "video/x-msvideo":            { label: "AVI",                  extension: "avi",   type: "video" },
  "video/x-ms-wmv":             { label: "WMV",                  extension: "wmv",   type: "video" },
  "video/3gpp":                 { label: "3GPP",                 extension: "3gp",   type: "video" },
  "video/3gpp2":                { label: "3GPP2",                extension: "3g2",   type: "video" },

  /* ───────────── Word ───────────── */
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                                { label: "Word (DOCX)",          extension: "docx",  type: "word" },
  "application/msword":         { label: "Word (DOC)",           extension: "doc",   type: "word" },
  "application/vnd.ms-word.document.macroenabled.12":
                                { label: "Word (DOCM)",          extension: "docm",  type: "word" },
  "application/vnd.openxmlformats-officedocument.wordprocessingml.template":
                                { label: "Word Template (DOTX)", extension: "dotx",  type: "word" },

  /* ───────────── Excel ───────────── */
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                                { label: "Excel (XLSX)",         extension: "xlsx",  type: "excel" },
  "application/vnd.ms-excel":   { label: "Excel (XLS)",          extension: "xls",   type: "excel" },
  "application/vnd.ms-excel.sheet.macroenabled.12":
                                { label: "Excel (XLSM)",         extension: "xlsm",  type: "excel" },
  "application/vnd.openxmlformats-officedocument.spreadsheetml.template":
                                { label: "Excel Template (XLTX)",extension: "xltx",  type: "excel" },

  /* ───────────── PowerPoint ───────────── */
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
                                { label: "PowerPoint (PPTX)",    extension: "pptx",  type: "ppt" },
  "application/vnd.ms-powerpoint":
                                { label: "PowerPoint (PPT)",     extension: "ppt",   type: "ppt" },
  "application/vnd.ms-powerpoint.presentation.macroenabled.12":
                                { label: "PowerPoint (PPTM)",    extension: "pptm",  type: "ppt" },
  "application/vnd.openxmlformats-officedocument.presentationml.template":
                                { label: "PowerPoint Template (POTX)", extension: "potx", type: "ppt" },
  "application/vnd.openxmlformats-officedocument.presentationml.slideshow":
                                { label: "PowerPoint Show (PPSX)", extension: "ppsx", type: "ppt" },

  /* ───────────── OneNote ───────────── */
  "application/onenote":        { label: "OneNote",              extension: "one",   type: "onenote" }
}


export const imageMimeTypes = Object.keys(mimeTypeMap).reduce((s, key) => {
  const entry = mimeTypeMap[key]
  if(entry.type === 'image') {
    s.push(key)
  }
  return s
}, [])

export const imageExtensionsRegex = new RegExp(`\\.(${imageMimeTypes
  .map(imt => imt
    .split('/')[1]
    .replace(/\./g, '\\.')
    .replace(/\+/g, '\\+')
  )
  .join('|')})\\s*$`, 'i')
  
export const addDomainsToPossiblyRelativeUrls = (url) => {
  const { domain } = getSiteConfig()
  return url
    .replace(/"\s*\/sites\//gi, `"${domain}/sites/`)
    .replace(/'\s*\/sites\//gi, `'${domain}/sites/`)
    .replace(/^\s*\/sites\//gi, `${domain}/sites/`)
    .replace(/"\s*\/_layouts\//gi, `"${domain}/_layouts/`)
    .replace(/'\s*\/_layouts\//gi, `'${domain}/_layouts/`)
    .replace(/^\s*\/_layouts\//gi, `${domain}/_layouts/`)
}

export const renderSpImageWebPart = async (wp, cache) => {
  const p   = wp.data.properties;
  const _img = wp.data.serverProcessedContent.imageSources
                  .find(o => o.key === 'imageSource')?.value;
  if (!_img) console.error('imageSource missing in web-part', wp)

  const fqImg = addDomainsToPossiblyRelativeUrls(_img)
  const imgRaw = _img ? await getThumbnailImage({ thumbnailWebUrl: fqImg }, cache) : ''
  const img = /^\s*blob:/i.test(imgRaw) ? '' : imgRaw

  const desiredW  = Math.round(p.resizeDesiredWidth
                     ?? p.imgWidth * p.resizeCoefficient
                     ?? p.imgWidth
                     ?? 0);

  const desiredH  = p.fixAspectRatio === false && p.imgHeight
                  ? Math.round(p.imgHeight * (desiredW / p.imgWidth))
                  : 'auto';

  const alignMap  = { Left: 'flex-start', Center: 'center', Right: 'flex-end' };
  const justify   = alignMap[p.alignment] || 'flex-start';

  const overlay   = (p.isOverlayTextVisible && p.overlayText?.trim())
                  ? buildOverlay(p)
                  : '';
  const imgTag = `<img src="${img}"
                       alt="${escapeAttr(p.altText || '')}"
                       style="border-radius:6px;max-width:100%;width:${desiredW}px;height:${desiredH};display:block;border:0;" />`;

  const maybeLinked = p.linkUrl
        ? `<a href="${escapeAttr(p.linkUrl)}" style="text-decoration:none;">${imgTag}</a>`
        : imgTag;

  const caption = p.captionText?.trim()
        ? `<figcaption style="margin-top:4px;font:14px/1.4 'Segoe UI',Arial,sans-serif;color:#323130;">${escapeHtml(p.captionText)}</figcaption>`
        : '';

  return `
<div style="display:flex;justify-content:${justify};">
  <figure style="margin:0;position:relative;">
    ${maybeLinked}
    ${overlay}
    ${caption}
  </figure>
</div>`.trim();
}

function buildOverlay(p) {
  const st      = p.overlayTextStyles || {};

  const txt     = st.textColor === 'light' ? '#ffffff' : '#000000';
  const weight  = st.isBold   ? '700' : '400';
  const fStyle  = st.isItalic ? 'italic' : 'normal';

  const boxClr  = st.textBoxColor === 'dark' ? '0,0,0' : '255,255,255';
  const boxOp   = typeof st.textBoxOpacity === 'number' ? st.textBoxOpacity : 0.54;

  return `<figcaption style="
      position:absolute;left:0;bottom:0;width:100%;
      box-sizing:border-box;padding:8px 12px;
      background:rgba(${boxClr},${boxOp});
      color:${txt};font-weight:${weight};font-style:${fStyle};
      font:20px/1.4 'Segoe UI',Arial,sans-serif;
      word-break:break-word;">
      ${escapeHtml(p.overlayText)}
    </figcaption>`;
}

function escapeHtml(str = '') {
  return str.replace(/[&<>"]/g, s =>
    ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[s]));
}
function escapeAttr(str = '') {
  return str.replace(/["&<>]/g, s =>
    ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;' }[s]));
}

export function toPascalCase(input) {
  return input
    .split(/[^a-zA-Z0-9]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join('');
}

export const carouselPaginationEnabled = (window, articleCount, totalAvailable, moreAvailable, currentIndex) => {
  let showNext, showPrevious = false
  if(!moreAvailable && articleCount > window) showPrevious = true
  if(window < articleCount) showNext = true
  if(totalAvailable > articleCount) showNext = true
  if(moreAvailable) showNext = true
  if(currentIndex > 0 && articleCount !== window) showPrevious = true
  return [showPrevious, showNext]
}

export const strToB64 = (str) => {
  const bytes    = new TextEncoder().encode(str);
  const ascii    = String.fromCharCode(...bytes);
  const base64   = btoa(ascii);
  return base64
}